import { computed, onUnmounted, ref } from "vue";
import { useIntervalWhenVisible } from "@/composables/useIntervalWhenVisible";
import { formatLockHolderName } from "@/utils";
const LOCKED = 423;
const RENEW_MS = 60000;
const PEEK_MS = 15000;
/** One record's edit lock on a page: hold and renew it, or view read-only while another user holds it. */
export function useEditLock(viewer) {
    let api = null;
    let key = null;
    let viewing = false;
    let disposed = false;
    const holds = ref(false);
    const free = ref(false);
    const holder = ref(null);
    const bannerText = computed(() => {
        if (free.value)
            return "The record is free. You can take over editing.";
        const name = formatLockHolderName(holder.value);
        return name ? `${name} is editing this record. View only.` : "Another user is editing this record. View only.";
    });
    const current = (target) => !disposed && target === api;
    const releaseOnUnload = () => {
        if (holds.value)
            api?.releaseOnUnload();
    };
    const view = (lock) => {
        if (disposed)
            return;
        holds.value = false;
        viewing = true;
        viewer.value = true;
        free.value = false;
        holder.value = lock?.user ?? null;
    };
    const demote = (error) => {
        const response = error?.response;
        if (response?.status !== LOCKED)
            return false;
        if (!viewer.value || response.data?.lock)
            view(response.data?.lock);
        return true;
    };
    const take = async () => {
        const target = api;
        const targetKey = key;
        if (target === null || !current(target))
            return false;
        try {
            await target.acquire();
        }
        catch (error) {
            if (!current(target))
                return false;
            if (demote(error))
                return false;
            throw error;
        }
        if (!current(target)) {
            if (key !== targetKey)
                await target.release().catch(() => undefined);
            return false;
        }
        holds.value = true;
        viewing = false;
        viewer.value = false;
        free.value = false;
        holder.value = null;
        return true;
    };
    const stop = async () => {
        window.removeEventListener("beforeunload", releaseOnUnload);
        const target = api;
        const held = holds.value;
        api = null;
        key = null;
        holds.value = false;
        free.value = false;
        holder.value = null;
        if (viewing)
            viewer.value = false;
        viewing = false;
        if (held && target)
            await target.release().catch(() => undefined);
    };
    const start = async (nextKey, next) => {
        if (disposed)
            return false;
        if (nextKey === key && holds.value)
            return true;
        await stop();
        if (disposed)
            return false;
        api = next;
        key = nextKey;
        window.addEventListener("beforeunload", releaseOnUnload);
        return take();
    };
    const dispose = () => {
        disposed = true;
        return stop();
    };
    const peek = async () => {
        const target = api;
        if (target === null || !current(target) || holds.value)
            return;
        try {
            const lock = await target.peek();
            if (!current(target) || holds.value)
                return;
            if (lock)
                view(lock);
            else
                free.value = true;
        }
        catch {
            return;
        }
    };
    useIntervalWhenVisible(() => holds.value && take().catch(() => undefined), RENEW_MS);
    useIntervalWhenVisible(() => viewer.value && peek(), PEEK_MS);
    onUnmounted(dispose);
    return { holds, free, bannerText, start, stop, takeOver: take, demote };
}
